import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ImageModule } from 'primeng/image';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { TutorService } from '../tutor.service';
import { CONSTANTS } from '../../../../core/constants';
import { GlobalFunctions } from '../../../../core/global-functions';
import { CommonService } from '../../../../core/common.service';
import { LocalStorageHelper } from '../../../../core/helpers/local-storage.helper';

@Component({
  selector: 'app-about-tutor',
  standalone: true,
  imports: [CommonModule, ImageModule, ReactiveFormsModule, FormsModule, ToastModule],
  templateUrl: './about-tutor.component.html',
  styleUrl: './about-tutor.component.scss',
})
export class AboutTutorComponent implements OnInit {
  profileImage: any = '';
  introducationVideo: any = '';
  selectedProfilePhoto: File | null = null;
  selectedIntroVideo: File | null = null;
  constants: any = CONSTANTS;
  profileDetailForm!: FormGroup;
  profileDetails: any;
  isAboutStepPending: boolean = true;

  isBtnLoading: boolean = false;

  constructor(
    private localStorage: LocalStorageHelper,
    private _tutorServices: TutorService,
    private _commonService: CommonService,
    private _globalFunctions: GlobalFunctions,
    private messageService: MessageService,
    private _router: Router,
    private _formBuilder: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getOnboardingStatus();
    this.getUserDetails();
  }

  getOnboardingStatus() {
    this._commonService.getProfileStatus().subscribe((result: any) => {
      if (result && result.success && result.data) {
        const aboutStep = result.data.find((s: any) => s.step_key === 'about');
        this.isAboutStepPending = aboutStep
          ? aboutStep.status === 'pending' || aboutStep.status === 'rejected'
          : true;
      }
    });
  }

  initForm() {
    this.profileDetailForm = this._formBuilder.group({
      photo: ['', [Validators.required]],
      introducation_video: ['', [Validators.required]],
      about_summary: ['', [Validators.required]],
      address_line_1: ['', [Validators.required]],
      address_line_2: ['', [Validators.required]],
      pincode: ['', [Validators.required]],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      country: ['', [Validators.required]],
    });
  }

  getUserDetails() {
    this._commonService.getProfile().subscribe({
      next: (result: any) => {
        if (result && result.success && result.data) {
          this.profileDetails = result.data;
          this._commonService.setProfileData(result.data); // Update local storage
          this.prepareProfileFormDetailsObj(result.data);
        }
      },
      error: (error: any) => {
        this._globalFunctions.errorHanding(error, this, true);
      },
    });
  }

  uploadImage(event: any) {
    const file = event.target.files[0];
    if (
      file &&
      file.type != 'image/jpeg' &&
      file.type != 'image/jpg' &&
      file.type != 'image/png'
    ) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please upload image in PNG or JPEG Formate' });
      return;
    }
    if (file) {
      this.selectedProfilePhoto = file;
      this.profileDetailForm.controls['photo'].setValue('selected');
      this.profileDetailForm.controls['photo'].markAsDirty();
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.profileImage = e.target.result;
        this.profileDetailForm.controls['photo'].setValue('selected'); // Set a dummy value for validation
      };
      reader.readAsDataURL(file);
    }
  }

  uploadintroducationVideo(event: any) {
    const file = event.target.files[0];
    if (file && !this.checkVideoType(file.type)) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please upload Video on video Formate' });
      return;
    }
    if (file) {
      this.selectedIntroVideo = file;
      this.profileDetailForm.controls['introducation_video'].setValue('selected');
      this.profileDetailForm.controls['introducation_video'].markAsDirty();
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.introducationVideo = e.target.result;
        this.profileDetailForm.controls['introducation_video'].setValue(
          'selected',
        ); // Set a dummy value for validation
      };
      reader.readAsDataURL(file);
    }
  }

  checkVideoType(fileType: any): boolean {
    let tempFileType: boolean = false;
    if (
      this.constants.videoTypes.filter((item: any) => item == fileType)
        ?.length > 0
    ) {
      tempFileType = true;
    } else {
      tempFileType = false;
    }
    return tempFileType;
  }

  saveProfile() {
    if (this.profileDetailForm.invalid) {
      this.profileDetailForm.markAllAsTouched();
      return;
    }

    // Optimization: If no changes, just navigate to the next step
    if (!this.isAboutStepPending && !this.profileDetailForm.dirty && !this.selectedProfilePhoto && !this.selectedIntroVideo) {
      this._router.navigate(['/tutor-signup/' + this.constants.tutorSubjectsUrl]);
      return;
    }

    if (
      this.isAboutStepPending &&
      (!this.selectedProfilePhoto || !this.selectedIntroVideo)
    ) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select both profile photo and introduction video for initial setup.' });
      return;
    }

    this.isBtnLoading = true;

    const formData = new FormData();
    formData.append(
      'about_summary',
      this.profileDetailForm.value.about_summary,
    );
    formData.append(
      'address_line_1',
      this.profileDetailForm.value.address_line_1,
    );
    formData.append(
      'address_line_2',
      this.profileDetailForm.value.address_line_2,
    );
    formData.append('pincode', this.profileDetailForm.value.pincode);
    formData.append('city', this.profileDetailForm.value.city);
    formData.append('state', this.profileDetailForm.value.state);
    formData.append('country', this.profileDetailForm.value.country);

    if (this.selectedProfilePhoto) {
      formData.append('profile_photo', this.selectedProfilePhoto);
    }
    if (this.selectedIntroVideo) {
      formData.append('introduction_video', this.selectedIntroVideo);
    }

    const apiCall = this.isAboutStepPending
      ? this._tutorServices.createProfile(formData)
      : this._tutorServices.updateProfileData(formData);

    apiCall.subscribe(
      (result: any) => {
        if (result && result.success) {
          this.isBtnLoading = false;
          this.selectedProfilePhoto = null;
          this.selectedIntroVideo = null;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: result.message });
          this._commonService.getProfile(false, true).subscribe(
            (profileResult: any) => {
              if (profileResult && profileResult?.success) {
                this._commonService.setProfileData(profileResult?.data);
                this._router.navigate([
                  '/tutor-signup/' + this.constants.tutorSubjectsUrl,
                ]);
              } else {
                this._globalFunctions.successErrorHandling(
                  profileResult,
                  this,
                  true,
                );
              }
            },
            (error: any) => {
              this._globalFunctions.errorHanding(error, this, true);
            },
          );
        } else {
          this.isBtnLoading = false;
          this._globalFunctions.successErrorHandling(result, this, true);
        }
      },
      (error: any) => {
        this.isBtnLoading = false;
        this._globalFunctions.errorHanding(error, this, true);
      },
    );
  }

  prepareProfileFormDetailsObj(tempObj: any = {}) {
    this.profileDetailForm.patchValue({
      photo: tempObj?.user?.profile_photo?.url || '',
      introducation_video: tempObj?.profile?.introduction_video?.url || '',
      about_summary: tempObj?.profile?.about_summary || '',
      address_line_1: tempObj?.profile?.address_line_1 || '',
      address_line_2: tempObj?.profile?.address_line_2 || '',
      pincode: tempObj?.profile?.pincode || '',
      city: tempObj?.profile?.city || '',
      state: tempObj?.profile?.state || '',
      country: tempObj?.profile?.country || '',
    });
    this.profileImage = tempObj?.user?.profile_photo?.url || '';
    this.introducationVideo = tempObj?.profile?.introduction_video?.url || '';
  }

  removePhoto() {
    this.profileImage = '';
    this.selectedProfilePhoto = null;
    this.profileDetailForm.controls['photo'].setValue('');
  }

  removeVideo() {
    this.introducationVideo = '';
    this.selectedIntroVideo = null;
    this.profileDetailForm.controls['introducation_video'].setValue('');
  }
}
